import type React from "react";
import { useEffect, useRef, useState } from "react";
import { MdSearch, MdClose } from "react-icons/md";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import toast from "react-hot-toast";
import { useGoogleMapsLoader } from "../../../contexts/GoogleMapsLoaderContext";

const libraries: "places"[] = ["places"];

interface AddressData {
  address: string;
  lat: number;
  lng: number;
  isValid: boolean;
}

interface AddressInputProps {
  value: string;
  onChange: (data: AddressData) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  borderRadius?: "sm" | "md" | "lg" | "xl" | "2xl" | "full" | string;
  height?: "sm" | "md" | "lg";
  bounds?: google.maps.LatLngBounds | null;
  apiKey?: string;
  onClick?: () => void;
}

export default function AddressInput({
  value,
  onChange,
  label = "Address",
  placeholder = "Enter or search address",
  error = false,
  required = false,
  disabled = false,
  className = "",
  containerClassName = "",
  inputClassName = "",
  labelClassName = "",
  borderRadius = "md",
  height = "md",
  bounds,
  apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  onClick,
}: AddressInputProps) {
  const [address, setAddress] = useState<string>(value || "");
  const [lat, setLat] = useState<number>(0);
  const [lng, setLng] = useState<number>(0);
  const [isAddressSelected, setIsAddressSelected] = useState<boolean>(!!value);
  const [hasAddressError, setHasAddressError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastInvalidInput, setLastInvalidInput] = useState<string>("");

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // const { isLoaded, loadError } = useJsApiLoader({
  //   id: "google-map-script",
  //   googleMapsApiKey: apiKey,
  //   libraries,
  // });
  const { isLoaded, loadError } = useGoogleMapsLoader();
  console.log("isLoaded", isLoaded);
  console.log("loadError", loadError);

  // Dynamic classes based on props
  const borderRadiusClass = borderRadius.startsWith("rounded-")
    ? borderRadius
    : {
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
        full: "rounded-full",
      }[borderRadius] || "rounded-md";

  const heightClass = {
    sm: "h-10",
    md: "h-12",
    lg: "h-14",
  }[height];

  useEffect(() => {
    setAddress(value || "");
    setIsAddressSelected(!!value);
  }, [value]);

  // Address type validation function
  const isValidAddressType = (types: string[] | undefined) => {
    if (!types) return false;
    // Allow plus codes
    if (types.includes("plus_code")) return true;
    // Allow specific address types
    const validTypes = [
      "street_address",
      "premise",
      "establishment",
      "point_of_interest",
      "mosque",
      "geocode",
      "subpremise",
      "route",
    ];
    return types.some((type) => validTypes.includes(type));
  };

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (
      !place ||
      !place.place_id ||
      !place.geometry ||
      !place.geometry.location
    ) {
      return;
    }

    setIsLoading(true);
    // const place = autocompleteRef.current.getPlace();
    // console.log(autocompleteRef);
    // console.log(place);
    // if (!place || !place.geometry || !place.geometry.location) {
    //   setHasAddressError(true);
    //   setIsAddressSelected(false);
    //   setAddress("");
    //   setLat(0);
    //   setLng(0);
    //   onChange({ address: "", lat: 0, lng: 0, isValid: false });
    //   setIsLoading(false);
    //   toast.error("Please select a valid address from the dropdown");
    //   return;
    // }

    // Validate address type
    if (!isValidAddressType(place.types)) {
      setHasAddressError(true);
      setIsAddressSelected(false);
      setAddress("");
      setLat(0);
      setLng(0);
      onChange({ address: "", lat: 0, lng: 0, isValid: false });
      setIsLoading(false);
      toast.error("Please enter a specific address, not a general area");
      return;
    }

    const selectedAddress = place.formatted_address || place.name || "";
    const latitude = place.geometry.location.lat();
    const longitude = place.geometry.location.lng();

    setAddress(selectedAddress);
    setLat(latitude);
    setLng(longitude);
    setHasAddressError(false);
    setIsAddressSelected(true);
    setIsLoading(false);
    setLastInvalidInput(""); // Clear last invalid input

    onChange({
      address: selectedAddress,
      lat: latitude,
      lng: longitude,
      isValid: true,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setAddress(inputValue);
    setIsAddressSelected(false);
    setLastInvalidInput(""); // Clear last invalid input when user types
    console.log(inputValue);
    if (!inputValue.trim()) {
      setHasAddressError(false);
      onChange({ address: "", lat: 0, lng: 0, isValid: false });
    } else {
      // Show error when user types but hasn't selected from dropdown
      setHasAddressError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      console.log("Enter pressed, address:", address);

      // If input is empty, return early
      if (!address?.trim()) {
        console.log("Address is empty, returning");
        return;
      }

      // If this is the same invalid input as last time, show error and return
      if (address.trim() === lastInvalidInput) {
        console.log("Same invalid input, showing error");
        setHasAddressError(true);
        setIsAddressSelected(false);
        toast.error("Please enter a specific address, not a general area");
        return;
      }

      // Check if Google Maps is loaded
      if (!window.google || !window.google.maps) {
        console.error("Google Maps not loaded");
        toast.error("Google Maps not loaded. Please refresh the page.");
        return;
      }

      console.log("Starting geocoding for:", address.trim());
      // Clear any existing error before starting the search
      setHasAddressError(false);
      setIsLoading(true);

      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: address.trim() }, (results, status) => {
          console.log("Geocoding results:", results, "Status:", status);
          setIsLoading(false);
          if (status === "OK" && results && results.length > 0) {
            const place = results[0];
            console.log("Place found:", place);

            // Validate address type
            if (!isValidAddressType(place.types)) {
              console.log("Invalid address type:", place.types);
              setHasAddressError(true);
              setIsAddressSelected(false);
              // Store this invalid input
              setLastInvalidInput(address.trim());
              toast.error(
                "Please enter a specific address, not a general area"
              );
              return;
            }

            // If we get here, the address is valid, so clear the last invalid input
            setLastInvalidInput("");
            const formatted = place.formatted_address;
            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();

            console.log(
              "Setting address:",
              formatted,
              "lat:",
              latitude,
              "lng:",
              longitude
            );
            setAddress(formatted);
            setLat(latitude);
            setLng(longitude);
            setHasAddressError(false);
            setIsAddressSelected(true);

            onChange({
              address: formatted,
              lat: latitude,
              lng: longitude,
              isValid: true,
            });
          } else {
            console.log("Geocoding failed:", status);
            setHasAddressError(true);
            setIsAddressSelected(false);
            // Store this invalid input
            setLastInvalidInput(address.trim());
            toast.error("Invalid address or Plus Code.");
          }
        });
      } catch (error) {
        console.error("Error creating geocoder:", error);
        setIsLoading(false);
        toast.error("Error searching for address. Please try again.");
      }
    }
  };

  const clearAddress = () => {
    if (disabled) {
      toast.error("Address field is disabled");
      return;
    }

    setAddress("");
    setLat(0);
    setLng(0);
    setIsAddressSelected(false);
    setHasAddressError(false);
    setLastInvalidInput(""); // Clear last invalid input
    onChange({ address: "", lat: 0, lng: 0, isValid: false });

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  if (loadError) {
    return (
      <div className={`w-full max-w-full ${containerClassName}`}>
        <label
          className={`block text-text-dark font-medium text-sm mb-2 ${labelClassName}`}
        >
          {label}
          {required && <span className="text-status-error ml-1">*</span>}
        </label>
        <div className="text-status-error text-sm">
          Failed to load Google Maps
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`w-full max-w-full ${containerClassName}`}>
        <label
          className={`block text-text-dark font-medium text-sm mb-2 ${labelClassName}`}
        >
          {label}
          {required && <span className="text-status-error ml-1">*</span>}
        </label>
        <div
          className={`
            border rounded-md px-4 flex items-center justify-center bg-ui-hover w-full
            ${borderRadiusClass}
            ${heightClass}
            ${error ? "border-error" : "border-ui-border"}
          `}
        >
          <div className="flex items-center gap-2 text-text-secondary text-sm">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            Loading Google Maps...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative w-full max-w-full ${containerClassName}`}
      onClick={onClick}
    >
      <label
        className={`block text-text-dark font-medium text-sm mb-2 ${labelClassName}`}
      >
        {label}
        {required && <span className="text-status-error ml-1">*</span>}
      </label>

      <div className="relative w-full max-w-full">
        {!isAddressSelected && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
            <MdSearch className="text-text-secondary text-lg" />
          </div>
        )}

        {isLoaded && (
          <Autocomplete
            onLoad={(ref) => {
              autocompleteRef.current = ref;
            }}
            onPlaceChanged={handlePlaceChanged}
            bounds={bounds || undefined}
            options={{
              types: ["address"],
              fields: [
                "name",
                "formatted_address",
                "place_id",
                "geometry",
                "types",
              ],
            }}
            className={`                ${
              disabled ? "bg-gray-600 cursor-not-allowed" : "bg-white"
            }
`}
          >
            <input
              ref={inputRef}
              type="text"
              value={address}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled || isLoading}
              className={`
                w-full max-w-full border transition-colors duration-200 text-sm text-text-primary placeholder-text-light focus:border-primary outline-none
                ${borderRadiusClass}
                ${heightClass}
                ${isAddressSelected ? "hidden" : "block"}
                ${!isAddressSelected ? "pl-10 pr-4" : "px-4"}
                ${
                  error || hasAddressError ? "border-error" : "border-ui-border"
                }
                ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
                ${isLoading ? "cursor-wait" : ""}
                ${inputClassName}
                ${className}
              `}
            />
          </Autocomplete>
        )}

        {isAddressSelected && address && (
          <div
            className={`
              border flex items-center justify-between px-4 text-sm text-text-primary  transition-colors duration-200 w-full max-w-full min-w-0
              ${borderRadiusClass}
              ${heightClass}
              ${error ? "border-error" : "border-ui-border"}
              ${disabled ? "bg-gray-100" : "bg-white"}
            `}
          >
            <span className="flex-1 truncate min-w-0 pr-2" title={address}>
              {address}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={clearAddress}
                className="text-text-secondary hover:text-text-primary transition-colors flex-shrink-0"
              >
                <MdClose className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {hasAddressError && !isAddressSelected && (
        <div className="text-status-error text-xs mt-1 ml-1 max-w-full break-words">
          No suggestions found. Press Enter to search for this address.
        </div>
      )}
    </div>
  );
}
